import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { catchError, map, Observable, throwError, firstValueFrom } from "rxjs";
import respListMyQuotes from '../../models/mis-cotizaciones/IMyQuotesList';
import { CotizacionResponse } from '../../models/mis-cotizaciones/cotizacion-response.model';
import { CotizacionFilter } from '../../models/mis-cotizaciones/cotizacion-filter.model';
import { Cotizacion } from '../../models/mis-cotizaciones/cotizacion.model';
import { SeguimientoModalResponse } from '../../models/mis-cotizaciones/seguimiento-modal.model';
import { SeguimientoModalRequest, SeguimientoResponse } from '../../models/mis-cotizaciones/seguimiento-request.model';
import { ApiEndpoint } from "../../services/servicioCotizador/api.endpoint";
import { LiquidacionIndividualItem } from '../../models/cotizador-cotizacion/consultar-cotizacion-guardada/response/consultar-cotizacion-guardada-response.model';
import { environment as ENV } from '../../../environments/environment';
import { URL_MAP } from '../../shared/urlMap';

/**
 * Interfaz para la respuesta de una cotización individual
 */
export interface CotizacionIndividualResponse {
    status: boolean;
    codeStatus: string;
    message: string;
    data: Cotizacion;
    errorMessage: string | null;
}

/**
 * Representa un beneficiario en el contexto de edición de cotización.
 * El campo version es de solo lectura desde el front — el backend lo gestiona.
 */
export interface BeneficiarioEdicionItem {
    idBeneficiario: number;
    idCotizacion: number;
    esContratante: 'S' | 'N';
    parentescoId: string;
    tipoDocumento?: string;
    numDocumento?: string;
    primerNombre: string;
    segundoNombre?: string;
    primerApellido: string;
    segundoApellido?: string;
    genero?: string;
    fechaNacimiento?: string;
    edad?: number;
    /** Versiones en las que apareció este beneficiario (ej: "1,2,3"). null = nunca enviado. */
    version: string | null;
    programas: any[];
    subcuentas: any[];
}

export interface CotizacionContratoInclusionDTO {
    idInclusion?: number;
    idCotizacion?: number;
    numeroContrato?: string;
    linea?: string;
    plan?: string;
    programa?: string;
    estadoMora?: string;
    asesorPoblacion?: string;
    esSeleccionado?: string;
}

/**
 * Datos completos de una cotización para edición (snapshot de versión o iniciar-edicion)
 */
export interface CotizacionEdicionData {
    idCotizacion: number;
    fechaCreacion: string;
    idAsesor: string;
    pasoActual: number;
    idTipoSolicitud: number | null;
    idTipoPlan: number | null;
    observaciones: string | null;
    idTipoCotizacion: number | null;
    idEstado: number;
    nombreEstado: string;
    esEditable: boolean;
    contratante: any | null;
    beneficiarios: BeneficiarioEdicionItem[];
    colectivo: any | null;
    liquidaciones?: any[];
    liquidacionesIndividual?: LiquidacionIndividualItem[];
    comparativos?: any[];
    contratosInclusion?: CotizacionContratoInclusionDTO[];
}

/**
 * Respuesta del endpoint iniciar-edicion y snapshots de versión
 */
export interface CotizacionEdicionResponse {
    status: boolean;
    codeStatus: string;
    message: string;
    data: CotizacionEdicionData;
    errorMessage: string | null;
}

/**
 * Versión de una cotización
 */
export interface CotizacionVersion {
    idVersion: number;
    idCotizacion: number;
    numeroVersion: number;
    fechaCreacion: string;
}

/**
 * Respuesta del endpoint de listado de versiones
 */
export interface VersionesCotizacionResponse {
    status: boolean;
    codeStatus: string;
    message: string;
    data: CotizacionVersion[];
    errorMessage: string | null;
}

export interface EvaluarVersionesData {
    versionCreada: boolean;
    numeroVersion: number | null;
    mensaje: string;
}

export interface EvaluarVersionesResponse {
    success: boolean;
    message: string;
    code: string;
    data: EvaluarVersionesData;
}

export interface CotizacionSeguimientoTrazabilidadRequest {
    idCotizacion: number | null;
    cedulaAsesor: string;
    nombreAsesor: string;
    idTipoAccion: number;
    idNombreAccion: number;
    valorAccion?: string;
}

export interface CotizacionSeguimientoTrazabilidadResponse {
    status: boolean;
    codeStatus: string;
    message: string;
    data: any;
    errorMessage: string | null;
}

export interface CotizacionParametroTrazabilidad {
    idParametro: number;
    tipoParametro: string;
    valorParametro: string;
}

@Injectable({
    providedIn: 'root'
})
export class NetworkingMyQuotation {

    networkingOutput?: any;
    
    private readonly apiUrlPaginado = `${ENV.HOST}${URL_MAP.API.COTIZADOR_COTIZACION_PAGINADO}`;
    private readonly apiUrlCotizacion = `${ENV.HOST}${URL_MAP.API.COTIZADOR_VENTA_GANADA}`;
    private readonly apiUrlAsesor = `${ENV.HOST}${URL_MAP.API.COTIZADOR_CODIGO_ASESOR}`;
    private readonly apiUrlTrazabilidad = `${ENV.HOST}${URL_MAP.API.TRAZABILIDAD}`;
    private readonly apiUrlParametrosTrazabilidad = `${ENV.HOST}${URL_MAP.API.PARAMETROS_TRAZABILIDAD}`;

    constructor(
        private httpClient: HttpClient,
        private apiEndpoint: ApiEndpoint
    ) { }

    /**
     * Obtiene el idAsesor a partir del número de documento
     * @param numeroDocumento Número de documento del asesor
     * @returns Promise con el idAsesor o null si no se encuentra
     */
    async obtenerIdAsesorPorDocumento(numeroDocumento: string): Promise<number | null> {
        try {
            const body = {
                nombreClave: 'getSpAsesorDocumento',
                jsonParametros: JSON.stringify({
                    prm_docu_asesor: numeroDocumento
                })
            };

            console.log('🔍 [NetworkingMyQuotation] Consultando idAsesor para documento:', numeroDocumento);
            
            const response: any = await firstValueFrom(this.httpClient.post(this.apiUrlAsesor, body));
            
            if (response.status && response.data && response.data.length > 0) {
                const idasesor = response.data[0].idasesor;
                console.log('✅ [NetworkingMyQuotation] idAsesor encontrado:', idasesor);
                return idasesor;
            } else {
                console.warn('⚠️ [NetworkingMyQuotation] No se encontró información del asesor');
                return null;
            }
        } catch (error) {
            console.error('❌ [NetworkingMyQuotation] Error al obtener idAsesor:', error);
            return null;
        }
    }

    /**una cotización individual por su ID
     * @param idCotizacion ID de la cotización a consultar
     * @returns Observable con la respuesta de la cotización
     */
    getCotizacionById(idCotizacion: string): Observable<CotizacionIndividualResponse> {
        const url = `${this.apiUrlCotizacion}/${idCotizacion}/edit`;
        console.log('📤 Consultando cotización:', url);
        
        return this.httpClient.get<CotizacionIndividualResponse>(url).pipe(
            map((response: CotizacionIndividualResponse) => {
                if (!response.status) {
                    throw new Error(response.errorMessage || 'Error al obtener la cotización');
                }
                return response;
            }),
            catchError(error => {
                let errorMessage: string;
                
                if (error.status === 404) {
                    errorMessage = "No se encontró la cotización solicitada";
                } else if (error.status === 500) {
                    errorMessage = "Lo sentimos, estamos experimentando problemas técnicos en este momento. Por favor, inténtelo de nuevo más tarde";
                } else if (error.status === 403 || error.status === 401) {
                    errorMessage = "No tiene permisos para acceder a esta cotización";
                } else {
                    errorMessage = error.error?.message || error.message || "Error al obtener la cotización";
                }
                
                return throwError(() => errorMessage);
            })
        );
    }

    /**
     * Inicia la edición de una cotización. Si está en estado ENVIADA, crea una nueva versión
     * y cambia el estado a GUARDADA. Para otros estados edita directamente.
     * @param idCotizacion ID de la cotización a editar
     */
    iniciarEdicion(idCotizacion: string | number): Observable<CotizacionEdicionResponse> {
        const url = `${this.apiUrlCotizacion}/cotizacion/${idCotizacion}/iniciar-edicion`;
        console.log('📤 Iniciando edición de cotización:', url);

        return this.httpClient.post<CotizacionEdicionResponse>(url, {}).pipe(
            map((response: CotizacionEdicionResponse) => {
                if (!response.status) {
                    throw new Error(response.errorMessage || 'Error al iniciar la edición');
                }
                return response;
            }),
            catchError(error => {
                let errorMessage: string;
                if (error.status === 404) {
                    errorMessage = "No se encontró la cotización solicitada";
                } else if (error.status === 500) {
                    errorMessage = "Lo sentimos, estamos experimentando problemas técnicos en este momento. Por favor, inténtelo de nuevo más tarde";
                } else if (error.status === 403 || error.status === 401) {
                    errorMessage = "No tiene permisos para editar esta cotización";
                } else {
                    errorMessage = error.error?.message || error.message || "Error al iniciar la edición de la cotización";
                }
                return throwError(() => errorMessage);
            })
        );
    }

    /**
     * Obtiene la lista de versiones de una cotización (ordenadas de más reciente a más antigua)
     * @param idCotizacion ID de la cotización
     */
    getVersionesCotizacion(idCotizacion: string | number): Observable<VersionesCotizacionResponse> {
        const url = `${this.apiUrlCotizacion}/cotizacion/${idCotizacion}/versiones`;
        console.log('📤 Consultando versiones de cotización:', url);

        return this.httpClient.get<VersionesCotizacionResponse>(url).pipe(
            map((response: VersionesCotizacionResponse) => {
                if (!response.status) {
                    throw new Error(response.errorMessage || 'Error al obtener las versiones');
                }
                return response;
            }),
            catchError(error => {
                let errorMessage: string;
                if (error.status === 404) {
                    errorMessage = "No se encontraron versiones para esta cotización";
                } else if (error.status === 500) {
                    errorMessage = "Lo sentimos, estamos experimentando problemas técnicos en este momento. Por favor, inténtelo de nuevo más tarde";
                } else if (error.status === 403 || error.status === 401) {
                    errorMessage = "No tiene permisos para acceder a esta información";
                } else {
                    errorMessage = error.error?.message || error.message || "Error al obtener las versiones";
                }
                return throwError(() => errorMessage);
            })
        );
    }

    /**
     * Obtiene el snapshot completo de una versión específica de una cotización
     * @param idCotizacion ID de la cotización
     * @param idVersion ID de la versión
     */
    getVersionById(idCotizacion: string | number, idVersion: number): Observable<CotizacionEdicionResponse> {
        const url = `${this.apiUrlCotizacion}/cotizacion/${idCotizacion}/versiones/${idVersion}`;
        console.log('📤 Consultando versión específica:', url);

        return this.httpClient.get<CotizacionEdicionResponse>(url).pipe(
            map((response: CotizacionEdicionResponse) => {
                if (!response.status) {
                    throw new Error(response.errorMessage || 'Error al obtener la versión');
                }
                return response;
            }),
            catchError(error => {
                let errorMessage: string;
                if (error.status === 404) {
                    errorMessage = "No se encontró la versión solicitada";
                } else if (error.status === 500) {
                    errorMessage = "Lo sentimos, estamos experimentando problemas técnicos en este momento. Por favor, inténtelo de nuevo más tarde";
                } else if (error.status === 403 || error.status === 401) {
                    errorMessage = "No tiene permisos para acceder a esta versión";
                } else {
                    errorMessage = error.error?.message || error.message || "Error al obtener la versión";
                }
                return throwError(() => errorMessage);
            })
        );
    }

    /**
     * Refresca la lista de beneficiarios de una cotización en edición.
     * Se usa después de guardar/editar un beneficiario para obtener la lista actualizada.
     * Usa GET /edit (NO re-llama a POST /iniciar-edicion).
     * @param idCotizacion ID de la cotización
     */
    /**
     * Llama al endpoint de evaluación de versión. Compara la data actual contra la última versión
     * guardada y crea una nueva versión solo si hay cambios reales.
     * Fire-and-forget: el caller no necesita manejar errores.
     */
    evaluarVersiones(idCotizacion: number): Observable<EvaluarVersionesResponse> {
        const url = `${this.apiUrlCotizacion}/cotizacion/${idCotizacion}/versiones/evaluar`;
        return this.httpClient.post<EvaluarVersionesResponse>(url, {});
    }

    refreshBeneficiariosEdicion(idCotizacion: string | number): Observable<CotizacionEdicionResponse> {
        const url = `${this.apiUrlCotizacion}/cotizacion/${idCotizacion}/edit`;
        return this.httpClient.get<CotizacionEdicionResponse>(url).pipe(
            map((response: CotizacionEdicionResponse) => {
                if (!response.status) {
                    throw new Error(response.errorMessage || 'Error al refrescar beneficiarios');
                }
                return response;
            }),
            catchError(error => {
                const msg = error.error?.message || error.message || 'Error al refrescar la lista de beneficiarios';
                return throwError(() => msg);
            })
        );
    }

    /**
     * Obtiene 
     * Obtiene las cotizaciones de un asesor con filtros
     * @param filter Filtros para la búsqueda
     * @returns Observable con la respuesta de cotizaciones
     */
    getCotizacionesByAsesor(filter: CotizacionFilter): Observable<CotizacionResponse> {
        // const url = this.apiEndpoint.URL_COTIZACIONES_PAGINADO();
        const url = this.apiUrlPaginado; // Temporal: URL quemada para pruebas
        console.log('URL de la API:', url);
        console.log('Filtro recibido en servicio:', filter);
        
        let params = new HttpParams();
        
        // Agregar idAsesor como parámetro
        if (filter.asesorId) {
            params = params.set('idAsesor', filter.asesorId);
            console.log('✅ idAsesor agregado a params:', filter.asesorId);
        } else {
            console.warn('⚠️ filter.asesorId está vacío o undefined');
        }
        
        // Agregar parámetros opcionales solo si tienen valor
        if (filter.busqueda) {
            params = params.set('busqueda', filter.busqueda);
        }
        if (filter.fechaDesde) {
            params = params.set('fechaDesde', filter.fechaDesde);
        }
        if (filter.fechaHasta) {
            params = params.set('fechaHasta', filter.fechaHasta);
        }
        if (filter.estado !== undefined && filter.estado !== null) {
            params = params.set('estado', filter.estado.toString());
        }
        if (filter.idProbabilidadCierre !== undefined && filter.idProbabilidadCierre !== null) {
            params = params.set('idProbabilidadCierre', filter.idProbabilidadCierre.toString());
        }
        if (filter.idTipoSolicitud && filter.idTipoSolicitud.length > 0) {
            filter.idTipoSolicitud.forEach(ts => {
                params = params.append('idTipoSolicitud', ts.toString());
            });
        }
        if (filter.lineaNegocio && filter.lineaNegocio.length > 0) {
            filter.lineaNegocio.forEach(ln => {
                params = params.append('lineaNegocio', ln);
            });
        }
        if (filter.page !== undefined) {
            params = params.set('page', filter.page.toString());
        }
        if (filter.size !== undefined) {
            params = params.set('size', filter.size.toString());
        }
        if (filter.sortBy) {
            params = params.set('sortBy', filter.sortBy);
        }
        if (filter.sortDirection) {
            params = params.set('sortDirection', filter.sortDirection);
        }
        
        console.log('📤 Parámetros HTTP finales:', params.toString());
        console.log('📤 URL completa:', `${url}?${params.toString()}`);
        
        return this.httpClient.get<CotizacionResponse>(url, { params }).pipe(
            map((response: CotizacionResponse) => {
                if (!response.status) {
                    throw new Error(response.errorMessage || 'Error al obtener las cotizaciones');
                }
                return response;
            }),
            catchError(error => {
                let errorMessage: string;
                
                if (error.status === 404) {
                    errorMessage = "No se encontraron cotizaciones con los filtros especificados";
                } else if (error.status === 500) {
                    errorMessage = "Lo sentimos, estamos experimentando problemas técnicos en este momento. Por favor, inténtelo de nuevo más tarde";
                } else if (error.status === 403 || error.status === 401) {
                    errorMessage = "No tiene permisos para acceder a esta información";
                } else {
                    errorMessage = error.error?.message || error.message || "Error al obtener las cotizaciones";
                }
                
                return throwError(() => errorMessage);
            })
        );
    }

    /**
     * Obtiene los datos de seguimiento para el modal de una cotización específica
     * @param idCotizacion ID de la cotización
     * @returns Observable con la respuesta de los datos de seguimiento
     */
    getSeguimientoModal(idCotizacion: number): Observable<SeguimientoModalResponse> {
        const url = `${this.apiUrlCotizacion}/${idCotizacion}/seguimiento/modal`;
        console.log('📤 Consultando seguimiento modal:', url);
        
        return this.httpClient.get<SeguimientoModalResponse>(url).pipe(
            map((response: SeguimientoModalResponse) => {
                if (!response.status) {
                    throw new Error(response.errorMessage || 'Error al obtener los datos de seguimiento');
                }
                return response;
            }),
            catchError(error => {
                let errorMessage: string;
                
                if (error.status === 404) {
                    errorMessage = "No se encontraron datos de seguimiento para esta cotización";
                } else if (error.status === 500) {
                    errorMessage = "Lo sentimos, estamos experimentando problemas técnicos en este momento. Por favor, inténtelo de nuevo más tarde";
                } else if (error.status === 403 || error.status === 401) {
                    errorMessage = "No tiene permisos para acceder a esta información";
                } else {
                    errorMessage = error.error?.message || error.message || "Error al obtener los datos de seguimiento";
                }
                
                return throwError(() => errorMessage);
            })
        );
    }

    /**
     * Crea un nuevo seguimiento para una cotización
     * @param request Datos del seguimiento a crear
     * @returns Observable con la respuesta del servidor
     */
    crearSeguimiento(request: SeguimientoModalRequest): Observable<SeguimientoResponse> {
        const url = `${this.apiUrlCotizacion}/seguimiento/modal`;
        console.log('📤 Creando seguimiento:', url);
        console.log('Datos enviados:', request);
        
        return this.httpClient.post<SeguimientoResponse>(url, request).pipe(
            map((response: SeguimientoResponse) => {
                if (!response.status) {
                    throw new Error(response.errorMessage || 'Error al crear el seguimiento');
                }
                return response;
            }),
            catchError(error => {
                let errorMessage: string;
                
                if (error.status === 400) {
                    errorMessage = error.error?.errorMessage || "Datos inválidos para crear el seguimiento";
                } else if (error.status === 404) {
                    errorMessage = "No se encontró la cotización especificada";
                } else if (error.status === 500) {
                    errorMessage = "Lo sentimos, estamos experimentando problemas técnicos en este momento. Por favor, inténtelo de nuevo más tarde";
                } else if (error.status === 403 || error.status === 401) {
                    errorMessage = "No tiene permisos para realizar esta acción";
                } else {
                    errorMessage = error.error?.errorMessage || error.error?.message || error.message || "Error al crear el seguimiento";
                }
                
                return throwError(() => errorMessage);
            })
        );
    }

    /**
     * Actualiza un seguimiento existente de una cotización
     * @param idGestion ID de la gestión a actualizar
     * @param request Datos del seguimiento a actualizar
     * @returns Observable con la respuesta del servidor
     */
    actualizarSeguimiento(idGestion: number, request: SeguimientoModalRequest): Observable<SeguimientoResponse> {
        const url = `${this.apiUrlCotizacion}/seguimiento/modal/${idGestion}`;
        console.log('📤 Actualizando seguimiento:', url);
        console.log('Datos enviados:', request);
        
        return this.httpClient.put<SeguimientoResponse>(url, request).pipe(
            map((response: SeguimientoResponse) => {
                if (!response.status) {
                    throw new Error(response.errorMessage || 'Error al actualizar el seguimiento');
                }
                return response;
            }),
            catchError(error => {
                let errorMessage: string;
                
                if (error.status === 400) {
                    errorMessage = error.error?.errorMessage || "Datos inválidos para actualizar el seguimiento";
                } else if (error.status === 404) {
                    errorMessage = "No se encontró el seguimiento a actualizar";
                } else if (error.status === 500) {
                    errorMessage = "Lo sentimos, estamos experimentando problemas técnicos en este momento. Por favor, inténtelo de nuevo más tarde";
                } else if (error.status === 403 || error.status === 401) {
                    errorMessage = "No tiene permisos para realizar esta acción";
                } else {
                    errorMessage = error.error?.errorMessage || error.error?.message || error.message || "Error al actualizar el seguimiento";
                }
                
                return throwError(() => errorMessage);
            })
        );
    }

    // Métodos antiguos - mantener por compatibilidad si aún se usan
    getListMyQuotes(query: string): Observable<respListMyQuotes> {
      const url = this.apiEndpoint.URL_SERVICE_QUOTER_MY_QUOTES_LIST();
      return this.httpClient.get<respListMyQuotes>(`${url}/${query}`).pipe(
        map((response: any) => response),
        catchError(error => {
          let errorMessage: string;
          if (error.status === 404) {
            errorMessage = "Lo sentimos, estamos experimentando problemas técnicos en este momento. Por favor, inténtelo de nuevo más tarde o póngase en contacto con el equipo de soporte para obtener ayuda adicional";
          } else if (error.status === 500) {
            errorMessage = "Lo sentimos, estamos experimentando problemas técnicos en este momento. Por favor, inténtelo de nuevo más tarde o póngase en contacto con el equipo de soporte para obtener ayuda adicional";
          } 
          else {
            errorMessage = error.error?.message || "El usuario no cuenta con permisos para ingresar a 'Mis Cotizaciones'.";
          }
          return throwError(() => errorMessage);
        })
      );
    }
  

    getRolAsesorSusbscribe(asesor: string): Observable<number> {
      const url = this.apiEndpoint.URL_SERVICE_QUOTER_MY_QUOTES();
      return this.httpClient.get<number>(`${url}/rol/${asesor}`).pipe(
        map((response: any) => response),
        catchError(error => {
          let errorMessage: string;
          if (error.status === 412) {
            errorMessage = 'No se pudo cargar los recursos.';
          } else if (error.status === 404) {
            errorMessage = 'Lo sentimos, estamos experimentando problemas técnicos en este momento. Por favor, inténtelo de nuevo más tarde o póngase en contacto con el equipo de soporte para obtener ayuda adicional';
          } else if (error.status === 0) {
            errorMessage = 'Lo sentimos, estamos experimentando problemas técnicos en este momento. Por favor, inténtelo de nuevo más tarde o póngase en contacto con el equipo de soporte para obtener ayuda adicional';
          } else {
            errorMessage = error.error?.message || "No se pudo obtener el rol del asesor.";
          }
          // Lanza directamente el mensaje de error sin envolver en un objeto Error
          return throwError(() => errorMessage);
        })
      );
    }

    /**
     * Descarga el PDF de una cotización colectiva.
     * TODO: reemplazar documento y anio por los valores reales del contratante cuando el endpoint definitivo esté disponible.
     * Endpoint: POST /cotizador/certificados/pdf/retefuente
     */
    descargarPdfCotizacionColectivo(
        idCotizacion: string,
        ciudadAsesor: string,
        porcentajeSubsidio: string,
        mostrarTodosEscenarios: boolean,
        aplicaAnexos: boolean,
        nombreJefe: string,
        cargoJefe: string
    ): Observable<Blob> {

        const url = `${ENV.HOST}${URL_MAP.API.PDF_COTIZACION_COLECTIVO}`;

        const body = {
            jsonParametros: JSON.stringify({
                P_ID_COTIZACION: idCotizacion,
                P_CIUDAD_ASESOR: ciudadAsesor,
                P_PORCENTAJE_SUBSIDIO: porcentajeSubsidio,
                P_MOSTRAR_TODOS_ESCENARIOS: mostrarTodosEscenarios,
                P_APLICA_ANEXOS: aplicaAnexos,
                P_NOMBRE_JEFE: nombreJefe,
                P_CARGO_JEFE: cargoJefe
            })
        };

        const headers = new HttpHeaders({
            Accept: '*/*',
            'Content-Type': 'application/json'
        });

        return this.httpClient.post(url, body, {
            headers,
            responseType: 'arraybuffer'
        }).pipe(
            map((response: ArrayBuffer) => {
                if (!response || response.byteLength === 0) {
                    throw new Error('El servicio no retornó contenido de PDF');
                }

                return new Blob([response], {
                    type: 'application/pdf'
                });
            }),
            catchError(error => throwError(() => error))
        );
    }

    /**
     * Descarga el PDF de una cotización individual.
     * TODO: reemplazar documento y anio por los valores reales del contratante cuando el endpoint definitivo esté disponible.
     * TODO: actualizar la URL cuando el backend exponga el endpoint específico para cotización individual.
     * Endpoint temporal: POST /cotizador/certificados/pdf/retefuente (mismo que colectivo)
     */
    descargarPdfCotizacionIndividual(
        nombre: string,
        telefono: string,
        correo: string,
        idCotizacion: string | number,
        pComparativo: string,
        cuotaInscripcion: string | number,
        pagoProporcionalAnticipado: string | number,
        precioBaseAnexos: string | number,
        tarifaAsociado  : string | number,
        p_ciudad_asesor: string,
    ): Observable<Blob> {

        const url = `${ENV.HOST}${URL_MAP.API.PDF_COTIZACION_INDIVIDUAL}`;
        const body = {
            jsonParametros: JSON.stringify({
                P_NOMBRE_ASESOR: nombre,
                P_TELEFONO_ASESOR: telefono,
                P_CORREO_ASESOR: correo,
                P_ID_COTIZACION: idCotizacion,
                P_COMPARATIVO: pComparativo,
                P_CUOTA_INSCRIPCION: cuotaInscripcion,
                P_PAGO_PROPORCIONAL_ANTICIPADO: pagoProporcionalAnticipado,
                P_PRECIO_BASE_ANEXOS: precioBaseAnexos,
                P_TARIFA_ASOCIADO: tarifaAsociado,
                P_CIUDAD_ASESOR: p_ciudad_asesor,
            })
        };

        const headers = new HttpHeaders({ Accept: '*/*', 'Content-Type': 'application/json' });
       // const headers = new HttpHeaders({ 'Accept': 'application/json' , 'Content-Type': 'application/json' });


        return this.httpClient.post(url, body, { headers, responseType: 'arraybuffer' }).pipe(
            map((response: ArrayBuffer) => {
                if (!response || response.byteLength === 0) {
                    throw new Error('El servicio no retornó contenido de PDF');
                }
                return new Blob([response], { type: 'application/pdf' });
            }),
            catchError(error => throwError(() => error))
        );
    }

    consultarParametrosTrazabilidad(
        tipoParametro: 'TIPO_ACCION' | 'NOMBRE_ACCION'
    ): Observable<CotizacionParametroTrazabilidad[]> {
        const url = `${this.apiUrlParametrosTrazabilidad}/${tipoParametro}`;

        console.log('📤 Consultando parámetros trazabilidad:', url);

        return this.httpClient.get<CotizacionParametroTrazabilidad[]>(url);
    }

    /**
     * Registra la trazabilidad de una acción realizada en seguimiento de cotizaciones.
     * @param request Datos de la acción realizada: botón, filtro o buscador.
     */
    registrarTrazabilidad(
        request: CotizacionSeguimientoTrazabilidadRequest
    ): Observable<CotizacionSeguimientoTrazabilidadResponse> {
        const url = this.apiUrlTrazabilidad;

        console.log('📤 Registrando trazabilidad:', url);
        console.log('Datos enviados:', request);

        return this.httpClient.post<CotizacionSeguimientoTrazabilidadResponse>(url, request).pipe(
            map((response: CotizacionSeguimientoTrazabilidadResponse) => {
                if (!response.status) {
                    throw new Error(response.errorMessage || response.message || 'Error al registrar la trazabilidad');
                }
                return response;
            }),
            catchError(error => {
                let errorMessage: string;

                if (error.status === 400) {
                    errorMessage = error.error?.errorMessage || error.error?.message || 'Datos inválidos para registrar la trazabilidad';
                } else if (error.status === 404) {
                    errorMessage = 'No se encontró la cotización relacionada para registrar la trazabilidad';
                } else if (error.status === 500) {
                    errorMessage = error.error?.errorMessage || 'Error interno al registrar la trazabilidad';
                } else if (error.status === 403 || error.status === 401) {
                    errorMessage = 'No tiene permisos para registrar la trazabilidad';
                } else {
                    errorMessage = error.error?.errorMessage || error.error?.message || error.message || 'Error al registrar la trazabilidad';
                }

                return throwError(() => errorMessage);
            })
        );
    }

}
